<template>
  <div class="header">
    <div class="back-button" @click="gotoTaskList()">
      <img src="/img/icons/back_arrow.png" />
    </div>
    <div class="breadcrumb-outer">
      <span
        v-if="!backToWorkInstructionsList"
        id="gotoProductList"
        @click="gotoProductList"
      >
        {{ productListText }}
      </span>
      <div
        v-if="backToWorkInstructionsList"
        class="breadcrumb breadcrumb-root"
        @click="gotoTaskList()"
      >
        <span class="breadcrumb-text">{{ $t(backToListLabelKey) }}</span>
      </div>
      <div
        v-if="backToWorkInstructionsList && breadcrumbs"
        class="breadcrumb last"
      >
        <span class="breadcrumb-separator">></span>
        <span class="breadcrumb-text">{{ breadcrumbs }}</span>
      </div>
      <template v-else>
        <b-tooltip
          :label="breadcrumbs"
          position="is-bottom"
          :triggers="['hover', 'click']"
          :multilined="true"
        >
          <div class="breadcrumb" @click="gotoTaskList()">
            <span class="breadcrumb-separator">></span>
            <span class="breadcrumb-text">{{ breadcrumbs }}</span>
          </div>
        </b-tooltip>
        <div class="breadcrumb last">
          <span class="breadcrumb-separator">></span>
          {{ $t('work_instructions.label') }}
        </div>
      </template>
    </div>

    <div class="buttons" v-if="!hideActions || !hidePackageDropdown">
      <template v-if="packages.length > 0 && !hidePackageDropdown">
        <b-dropdown aria-role="list" position="is-bottom-left">
          <template #trigger>
            <b-button
              :label="
                currentPackage
                  ? currentPackage.title
                  : $t('select_work_instruction_package')
              "
              expanded
              type="is-primary"
              icon-right="menu-down"
            />
          </template>

          <b-dropdown-item
            aria-role="listitem"
            v-for="wiPackage in packages"
            :key="wiPackage._id"
            :value="wiPackage._id"
            @click="onPackageChange(wiPackage)"
            :position="'is-bottom-left'"
          >
            <h3>{{ wiPackage.title }}</h3>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <b-button
        v-if="!hideActions && packages.length > 0 && !isInspectorRoute"
        type="is-primary"
        icon-left="microsoft-excel"
        @click="onImportPackage"
      >
        Import WI
      </b-button>

      <b-button
        v-if="!hideActions && packages.length > 0 && !isInspectorRoute"
        type="is-primary"
        icon-left="microsoft-excel"
        @click="onExportPackage"
      >
        Export WI
      </b-button>

      <b-button
        v-if="!hideActions && !isInspectorRoute"
        type="is-info"
        @click="onAddWorkInstructionPackage"
      >
        {{ $t('add_work_instruction') }}
      </b-button>

      <b-button
        v-if="!hideActions && packages.length > 0 && isPublisherOrEditorRoute"
        type="is-danger"
        icon-left="delete"
        @click="onDeleteWorkInstructionPackage"
      >
        {{ $t('delete') }}
      </b-button>
    </div>
  </div>
</template>
<script>
import JobViewMixin from '../../../mixins/Shared/JobViewMixin';
import {
  GET_WORK_INSTRUCTION_PACKAGES,
  CURRENT_WORK_INSTRUCTION_PACKAGE,
} from '../../../store/workInstruction/getters/getterTypes';
import { LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE } from '../../../store/workInstruction/mutations/mutationTypes';

export default {
  name: 'WorkInstructionsHeader',
  mixins: [JobViewMixin],
  props: {
    hidePackageDropdown: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    customBreadcrumbs: {
      type: String,
      default: '',
    },
    forceProductListText: {
      type: String,
      default: '',
    },
    backToWorkInstructionsList: {
      type: Boolean,
      default: false,
    },
    backToListRouteName: {
      type: String,
      default: 'editor-work-instructions-list',
    },
    backToListLabelKey: {
      type: String,
      default: 'work_instructions.label',
    },
  },

  data() {
    return {
      frameTypesNamesFormatted: [],
    };
  },

  computed: {
    packages() {
      return this.$store.getters[GET_WORK_INSTRUCTION_PACKAGES];
    },

    currentPackage() {
      return this.$store.getters[CURRENT_WORK_INSTRUCTION_PACKAGE];
    },
    isInspectorRoute() {
      return this.$route.name.includes('inspector');
    },

    isPublisherOrEditorRoute() {
      return (
        this.$route.name.includes('publisher') ||
        this.$route.name.includes('editor')
      );
    },

    productListText() {
      if (this.forceProductListText) {
        return this.forceProductListText;
      }

      if (this.$route.name.includes('editor')) {
        return this.$t('product_template_list');
      }
      if (this.$route.name.includes('publisher')) {
        return this.$t('project_list');
      }
      return this.$t('work_order_list');
    },

    breadcrumbsPublisher() {
      const parts = [
        this.currentJob?.title,
        this.currentJob?.customerInfoText,
        this.currentJob?.projectInfoText,
        this.currentJob?.serialNumber,
        this.currentJob?.workOrderNumber,
      ].filter(Boolean);

      return parts.join(' / ');
    },

    breadcrumbs() {
      if (this.customBreadcrumbs) {
        return this.customBreadcrumbs;
      }

      if (this.$route.name.includes('editor')) {
        this.getAndFormatFrameTypes();
        const parts = [
          this.currentJob?.title,
          this.frameTypesNamesFormatted,
          this.currentJob?.userEnteredPartNumber,
          this.currentJob?.documentNumber,
          this.currentJob?.revision,
        ].filter(Boolean);

        return parts.join(' / ');
      }

      const parts = [
        this.currentJob?.title,
        this.currentJob?.customerInfoText,
        this.currentJob?.projectInfoText,
        this.currentJob?.serialNumber,
        this.currentJob?.workOrderNumber,
      ].filter(Boolean);

      return parts.join(' / ');
    },
  },

  methods: {
    getAndFormatFrameTypes() {
      if (
        typeof this.currentJob.frameType === 'undefined' ||
        !this.currentJob.frameType
      )
        return '';
      if (
        this.currentJob &&
        this.currentJob.frameType &&
        typeof this.currentJob.frameType !== 'string' &&
        this.currentJob.frameType.length > 1
      ) {
        this.currentJob.frameType.forEach((frameType, index) => {
          let formattedName;
          if (index !== 0) {
            formattedName = ` / ${frameType.text}`;
            this.frameTypesNamesFormatted.push(formattedName);
          } else {
            this.frameTypesNamesFormatted.push(frameType.text);
          }
        });
      } else if (typeof this.currentJob.frameType === 'string') {
        this.frameTypesNamesFormatted = this.currentJob.frameType;
      } else {
        this.frameTypesNamesFormatted = this.currentJob.frameType[0].text;
      }
    },
    onPackageChange(event) {
      const selectedPackage = this.packages.find(
        (pkg) => pkg._id === event._id
      );
      this.$store.commit(
        LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
        selectedPackage || null
      );
    },

    gotoProductList() {
      this.isLoading = true;
      if (this.$route.name.includes('editor')) {
        this.$router.push({
          name: 'editor-list',
        });
      } else if (this.$route.name.includes('inspector')) {
        this.$router.push({
          name: 'inspector-list',
        });
      } else if (this.$route.name.includes('publisher')) {
        this.$router.push({
          name: 'publisher-list',
        });
      } else {
        this.$router.push({
          name: 'report-list',
        });
      }
    },
    gotoTaskList() {
      if (this.backToWorkInstructionsList) {
        this.$router.push({
          name: this.backToListRouteName,
        });
        return;
      }

      if (this.$route.name.includes('editor')) {
        this.$router.push({
          name: 'editor-tasks',
          params: { id: this.currentJob._id },
        });
      } else if (this.$route.name.includes('inspector')) {
        this.$router.push({
          name: 'inspector-tasks',
          params: { id: this.currentJob._id },
        });
      } else {
        this.$router.push({
          name: 'publisher-tasks',
          params: { id: this.currentJob._id },
        });
      }
    },
    onAddWorkInstructionPackage() {
      this.$emit('onAdd');
    },
    onImportPackage() {
      this.$emit('onImport');
    },
    onExportPackage() {
      this.$emit('onExport');
    },
    onDeleteWorkInstructionPackage() {
      this.$emit('onDelete');
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  z-index: 2;
  font-family: 'Siemens Sans';
  display: inline-flex;
  position: relative;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  .back-button {
    display: inline-flex;
    width: 80px;
    height: 100%;
    cursor: pointer;
    img {
      width: 25px;
      margin: auto;
      &:hover {
        filter: brightness(0%);
      }
    }
  }

  .breadcrumb-outer {
    flex: 1 1 auto;
    min-width: 0;
    display: inline-flex;
  }

  .breadcrumb-outer ::v-deep .b-tooltip {
    min-width: 0;
    flex: 0 1 auto;
  }

  #gotoProductList,
  .breadcrumb {
    height: 50px;
    display: flex;
    align-items: center;
    margin: auto 0;
    overflow: hidden;
    cursor: pointer;
    color: #ccc;
    &:hover {
      color: #333;
    }
  }

  .breadcrumb {
    min-width: 0;
  }

  .breadcrumb-root {
    color: #333;
    font-weight: 600;
  }

  .breadcrumb-root .breadcrumb-text {
    color: #333;
    font-weight: 600;
  }

  .breadcrumb-text {
    font-size: 18px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .breadcrumb-separator {
    font-size: 18px;
    margin: 0 6px;
    flex-shrink: 0;
  }

  .last {
    color: #333;
    white-space: nowrap;
    flex-shrink: 0;

    .breadcrumb-text {
      color: #333;
      font-weight: bold;
    }

    .breadcrumb-separator {
      color: #333;
    }
  }
  #gotoProductList {
    color: #ccc;
    min-width: 90px;
    flex-shrink: 0;
    white-space: nowrap;
    &:hover {
      color: inherit;
      cursor: pointer;
    }
  }

  .path {
    display: inline-flex;
  }
}
.buttons {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  margin-right: 10px;
}
.dropdown {
  margin-top: 5px;
  margin-right: 10px;
}
</style>
